import { Component, ErrorInfo, FunctionComponent, ReactNode, Suspense, lazy } from "react";
import * as Sentry from "@sentry/react";
import { Pane, Text } from "evergreen-ui";

// Code-split: FmsChatPage pulls in recharts + its ~25-package d3 tree, which
// should not land in the app-wide bundle for a beta-gated page. The named
// export requires the `.then` adapter since `React.lazy` expects a module
// with a default export.
const LazyFmsChatPage = lazy(() => import("../pages/FmsChatPage").then(m => ({ default: m.FmsChatPage })));

// Guards against a React.lazy import REJECTION - e.g. a stale chunk 404 after
// a deploy rotation, or the server being down - which otherwise throws during
// render with no boundary above it and unmounts the whole app tree (blank
// page). Hand-rolled (not Sentry.ErrorBoundary) to match the existing
// ChartErrorBoundary pattern in FmsChatChart.tsx: captures to Sentry the same
// way, but renders a short, user-visible fallback message here instead of
// null - a blank page reads as a dead app, not just a failed chart.
class FmsChatRouteErrorBoundary extends Component<{ children?: ReactNode }, { failed: boolean }> {
    public state = { failed: false };

    public static getDerivedStateFromError(): { failed: boolean } {
        return { failed: true };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        Sentry.captureException(error, {
            contexts: { react: { componentStack: errorInfo.componentStack } },
        });
    }

    public render(): ReactNode {
        if (this.state.failed) {
            return (
                <Pane padding={24}>
                    <Text>Couldn&apos;t load chat. Please reload.</Text>
                </Pane>
            );
        }
        return this.props.children;
    }
}

// Exported for the error-boundary unit test (a plain throwing child, no
// Suspense/lazy machinery involved).
export { FmsChatRouteErrorBoundary };

// `routes` (Routes.ts) is plain data consumed by App.tsx's render loop and
// stays a .ts module (no JSX), so this wrapper - the error boundary scoped
// around the Suspense boundary scoped around the lazy import - lives here
// instead and is imported into Routes.ts as the route's `component`.
export const FmsChatPageRoute: FunctionComponent<any> = (props) => (
    <FmsChatRouteErrorBoundary>
        <Suspense fallback={null}>
            <LazyFmsChatPage {...props} />
        </Suspense>
    </FmsChatRouteErrorBoundary>
);
